import React, { useContext, useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

import constants from 'config/constants'
import useAxios from 'hooks/useAxios'
import { getSurveyTokenFromId } from 'reducers'
import { surveySettingContext } from 'pages/SurveySettings/context'
import { TooltipLabel } from 'components/TooltipLabel'
import { toast } from 'libs/toast'
import { Input, Form, Button, Grid, FormField, Dropdown } from 'semantic-ui-react'
import { Loader } from 'shared/components/Loader'
import { TrialWarningMessage } from 'components/TrialWarningMessage'

export default function EventBasedEmailDeliverySettingContainer({ connector }) {
  const [suppressionDay, setSuppressionDay] = useState()
  const [delayHour, setDelayHour] = useState()
  const [connectorId, setConnectorId] = useState(connector?.id || undefined)
  // PET-2084: feed the survey-settings unsaved-changes guard. Track dirty in a
  // LOCAL flag from the change handlers (a plain setState is safe), and sync it
  // to the shared context in an effect — NOT synchronously inside onChange,
  // which re-rendered controlled inputs and broke editing (see generic-embed).
  const { setContext } = useContext(surveySettingContext)
  const [isFormDirty, setIsFormDirty] = useState(false)
  useEffect(() => {
    setContext({ isSurveyBuilderDirty: isFormDirty })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormDirty])

  function useSurveyToken() {
    const { surveyId } = useParams()
    return useSelector((state) => getSurveyTokenFromId(state, surveyId))
  }

  const surveyToken = useSurveyToken()

  const getApiOptionsApi = useAxios({
    url: `survey/survey/${surveyToken}/survey-settings/`,
  })
  const saveApiOptionsApi = useAxios({
    method: 'patch',
    data: {
      suppression_day: suppressionDay,
      delay_hour: delayHour,
    },
    url: `survey/survey/${surveyToken}/survey-settings/`,
    skip: true,
  })

  const { surveyId } = useParams()
  const saveConnector = useAxios({
    method: 'patch',
    url: `account/survey/${surveyId}/`,
    skip: true,
  })

  const { data: connectors, loading: loadingConnectors } = useAxios({
    url: `cyclr/account/connectors/authenticated/`,
  })

  const connectorOptions =
    connectors?.results?.map(({ id, name }) => ({
      key: id,
      text: name,
      value: id,
    })) || []

  const endpoint = constants.backendHost + '/api/v1/surveys/' + surveyToken + '/email/'

  const buttonRef = useRef()
  useSetCurrentSaveFunction(buttonRef)
  useSetHeaderSaveButtonLoading(saveApiOptionsApi.loading || !getApiOptionsApi.data)

  function useSetCurrentSaveFunction(buttonRef) {
    const { setContext } = useContext(surveySettingContext)

    useEffect(() => {
      function saveApiOptions() {
        if (buttonRef.current) {
          buttonRef.current.click()
        }
      }

      setContext({
        currentSaveFunction: saveApiOptions,
      })
    }, [buttonRef, setContext])
  }

  function useSetHeaderSaveButtonLoading(loading) {
    const { setContext } = useContext(surveySettingContext)
    useEffect(() => {
      setContext({
        isSubmitting: loading,
      })

      return () => {
        setContext({
          isSubmitting: false,
        })
      }
    }, [loading, setContext])
  }

  function copyTextToclipBoard(event) {
    event.preventDefault()
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(endpoint)
      toast.success('Boom! Endpoint copied to clipboard.')
    } else {
      toast.error('The Clipboard API is not available.')
    }
  }

  function handleSubmit(e) {
    e.preventDefault()

    Promise.all([
      saveApiOptionsApi.fetch(),
      saveConnector.fetch({ data: { connector: connectorId || null } }),
    ])
      .then(() => {
        setIsFormDirty(false)
        toast.success('Event-based email settings were successfully updated.')
      })
      .catch(() =>
        toast.error(
          'Failed to save audience rule settings. Please try again or contact Simplesat support.'
        )
      )
  }

  useEffect(() => {
    if (surveyToken && getApiOptionsApi.data) {
      const { suppression_day, delay_hour } = getApiOptionsApi.data
      setSuppressionDay(suppression_day)
      setDelayHour(delay_hour)
    }
  }, [surveyToken, getApiOptionsApi.data])

  return (
    <Form onSubmit={(e) => handleSubmit(e)}>
      <Grid stackable>
        <Grid.Column className="twelve wide">
          {suppressionDay != undefined && delayHour != undefined ? (
            <React.Fragment>
              <p>
                Automate survey delivery via API, triggered by events such as successful checkouts
                or ticket closures.
              </p>
              <TrialWarningMessage />
              <FormField>
                <label>To send an event-based email survey:</label>
                <ol className="numbered-list">
                  <li>Make a POST API request to the provided endpoint.</li>
                  <li>Ensure the request body contains the customer's email.</li>
                  <li>
                    You may also include additional attributes like company, custom attributes, or
                    team member name for a personalized survey email.
                  </li>
                </ol>
              </FormField>
              <FormField>
                <a
                  href="https://help.simplesat.io/en/articles/2220503-direct-email-surveys#trigger-a-survey-from-your-own-system"
                  target="_blank"
                  rel="noopener"
                >
                  <i className="icon far fa-book" />
                  Learn more about sending event-based email surveys
                </a>
              </FormField>
              <FormField>
                <label>Endpoint</label>
                <Input
                  style={{ width: 440 }}
                  action={{
                    icon: 'far fa-clone',
                    onClick: (event) => copyTextToclipBoard(event)
                  }}
                  value={endpoint}
                  readOnly
                />
              </FormField>
              <FormField>
                <label>Request body</label>
                <EventBasedCodeSnippet />
              </FormField>

              <Grid stackable>
                <Grid.Column tablet={16} computer={7}>
                  <FormField>
                    <label>
                      <TooltipLabel
                        label="Suppression"
                        descriptionTitle="Suppression"
                        descriptionContent="Limit frequency of emails by ensuring each customer only receives one survey within a set timeframe. This helps prevent survey fatigue."
                        learnMoreLink="https://help.simplesat.io/en/articles/2220503-direct-email-surveys#suppression"
                      />
                    </label>

                    <FormField inline>
                      <Input
                        fluid
                        label="days"
                        labelPosition="right"
                        value={suppressionDay}
                        name="suppression-day"
                        type="number"
                        onChange={(e) => {
                          if (e.target.valueAsNumber >= 0 && e.target.valueAsNumber <= 9999) {
                            setSuppressionDay(e.target.valueAsNumber)
                            setIsFormDirty(true)
                          }
                        }}
                      />
                    </FormField>
                  </FormField>

                  <FormField>
                    <label>
                      <TooltipLabel
                        label="Delay"
                        descriptionTitle="Delay"
                        descriptionContent="Define the number of hours to wait before sending the survey after the initial trigger."
                        learnMoreLink="https://help.simplesat.io/en/articles/2220503-direct-email-surveys#delay"
                      />
                    </label>
                    <FormField inline>
                      <Input
                        fluid
                        label="hours"
                        labelPosition="right"
                        value={delayHour}
                        name="delay-hour"
                        type="number"
                        onChange={(e) => {
                          if (e.target.valueAsNumber >= 0 && e.target.valueAsNumber <= 9999) {
                            setDelayHour(e.target.valueAsNumber)
                            setIsFormDirty(true)
                          }
                        }}
                      />
                    </FormField>
                  </FormField>

                  <FormField>
                    <TooltipLabel
                      label="Connected service"
                      descriptionTitle="Connected service"
                      descriptionContent="Connect this survey with an authenticated integration to enable ticket-level tracking. When a external ID (such as a ticket number or product SKU) is included in the event-based email request, Simplesat can sync feedback with your helpdesk, updating ticket details, adding private notes, and more based on your integration settings."
                    />
                    <Dropdown
                      fluid
                      placeholder="Select connected service"
                      search
                      selection
                      clearable
                      value={connectorId}
                      onChange={(_, { value }) => {
                        setConnectorId(value)
                        setIsFormDirty(true)
                      }}
                      options={connectorOptions}
                      loading={loadingConnectors || saveConnector.loading}
                      disabled={loadingConnectors || saveConnector.loading}
                    />
                  </FormField>
                </Grid.Column>
              </Grid>
            </React.Fragment>
          ) : (
            <Loader withContainer containerHeight={150} />
          )}
        </Grid.Column>
      </Grid>
      <button hidden ref={buttonRef} />
    </Form>
  )
}

const EventBasedCodeSnippet = () => {
  const embedScript = `{
    "customer": {
      "name": "Example Customer",
      "email": "customer@example.com", // Email is required to send. All other fields are optional.
      "company": "Example Co.",
      "custom_attributes": {
        "Plan": "Pro"
      }
    },
    "team_member": {
      "id": 1234,
      "email": "teammember@example.com", // Email or ID are required in order to associate the team member with a response.
      "name": "Team Member",
      "custom_attributes": {
        "Group": "Support"
      }
    },
    "ticket": {
      "id": "ticketid", // Random ticket IDs will be assigned if this field is omitted.
      "subject": "Ticket subject",
      "custom_attributes": {
        "status": "closed"
      }
    }
}`

  const embedCodeRef = useRef()
  function selectCode(e) {
    e.target.select()
  }
  return (
    <Form>
      <Form.Field>
        <textarea
          ref={embedCodeRef}
          readOnly
          rows={12}
          className="w-full"
          value={embedScript}
          onClick={selectCode}
        />
      </Form.Field>
      <CopyEmbedCodeButton embedCodeRef={embedCodeRef} />
    </Form>
  )
}

const CopyEmbedCodeButton = ({ embedCodeRef }) => {
  const TIMEOUT_MS = 2000
  const [overriddenButtonLabel, setOverriddenButtonLabel] = useDisappearingText('', TIMEOUT_MS)

  function handleClick(e) {
    e.preventDefault()
    copyWidgetCode()
    setOverriddenButtonLabel('Copied!')
  }

  function copyWidgetCode() {
    // Newer standard to replace `execCommand`
    if (navigator.clipboard && embedCodeRef.current) {
      navigator.clipboard.writeText(embedCodeRef.current.value)
    } else {
      embedCodeRef.current.select()
      document.execCommand('copy')
    }
  }

  return (
    <Button size="large" fluid primary onClick={handleClick}>
      {overriddenButtonLabel || 'Copy request body'}
    </Button>
  )
}

function useDisappearingText(initialText, timeout) {
  const [text, setText] = useState(initialText)
  const initialTimerId = 0
  const timerRef = useRef(initialTimerId)

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current)
    }
  }, [])

  function setTextWithTimeout(text) {
    setText(text)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setText('')
    }, timeout)
  }

  return [text, setTextWithTimeout]
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/EventBassedEmailDeliverySetting/container.js